import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, SkeletonRows, SkeletonCards, ErrorMessage, usePageMeta } from '../components/ui';
import { deriveTier, initial } from '../data/meta';
import MapCard from '../components/MapCard';
import AdSlot from '../components/AdSlot';

function Dashboard() {
  usePageMeta({});
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [brawlers, setBrawlers] = useState([]);
  const [maps, setMaps] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, br, mp, lb, nw] = await Promise.all([
        api.getOverview(),
        api.getBrawlers('win_rate'),
        api.getMaps(),
        api.getLeaderboard('trophies', 5).catch(() => []),
        api.getPosts().catch(() => []),
      ]);
      setOverview(ov);
      setBrawlers(br.map((b) => ({ ...b, tier: deriveTier(b.win_rate) })));
      setMaps(mp);
      setTopPlayers(lb);
      setNews(nw.slice(0, 3));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page"><div className="wrap"><ErrorMessage message={error} onRetry={load} /></div></div>;
  if (loading) {
    return (
      <div className="page"><div className="wrap">
        <div className="stat-ribbon">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="srib"><span className="sk sk-line" style={{ width: '55%', height: 22 }} /><span className="sk sk-line" style={{ width: '70%', marginTop: 10 }} /></div>
          ))}
        </div>
        <SkeletonRows rows={6} />
        <div style={{ marginTop: 24 }}><SkeletonCards count={3} /></div>
      </div></div>
    );
  }

  const byWin = brawlers.slice().sort((a, b) => (b.win_rate ?? 0) - (a.win_rate ?? 0));
  const sTier = byWin.filter((b) => b.tier === 'S');
  const spotlight = (sTier.length >= 4 ? sTier : byWin).slice(0, 4);
  const mostPicked = brawlers.slice().sort((a, b) => (b.pick_rate ?? 0) - (a.pick_rate ?? 0)).slice(0, 5);
  const hotMaps = maps.slice(0, 3);

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Meta pulse · live data</div>
            <h1>What's strong right now</h1>
            <div className="sub">
              Your competitive briefing — the top of the meta, what's getting picked, and the
              busiest maps in rotation.
            </div>
          </div>
        </div>

        <div className="stat-ribbon">
          <div className="srib"><div className="v">{(overview?.total_battles ?? 0).toLocaleString()}</div><div className="l">Battles tracked</div></div>
          <div className="srib"><div className="v">{(overview?.total_players ?? 0).toLocaleString()}</div><div className="l">Players ranked</div></div>
          <div className="srib"><div className="v">{overview?.total_brawlers ?? 0}</div><div className="l">Brawlers</div></div>
          <div className="srib"><div className="v">{(overview?.total_events ?? 0).toLocaleString()}</div><div className="l">Maps</div></div>
        </div>

        <div className="dash-grid">
          <div className="panel">
            <div className="panel-head">
              <h3><span className="ic win">★</span>Top of the meta — S tier</h3>
              <span className="more" onClick={() => navigate('/brawlers')}>Full tier list →</span>
            </div>
            <div className="spotlight">
              {spotlight.map((b) => (
                <div key={b.brawler_id} className="spot-card" onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                  <Avatar name={b.brawler_name} size={64} />
                  <div className="nm">{b.brawler_name}</div>
                  <div className="wr">{b.win_rate != null ? parseFloat(b.win_rate).toFixed(1) : '—'}% <span>win</span></div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3><span className="ic win">📈</span>Most picked</h3>
              <span className="more" onClick={() => navigate('/meta-report')}>This week's report →</span>
            </div>
            <div className="mover-list">
              {mostPicked.map((b) => (
                <div key={b.brawler_id} className="mover-row" onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                  <Avatar name={b.brawler_name} size={38} />
                  <span className="nm">{b.brawler_name}</span>
                  <span className="pick">{b.pick_rate != null ? parseFloat(b.pick_rate).toFixed(1) : '—'}% <span>pick</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <AdSlot />

        {topPlayers.length > 0 && (
          <div className="panel" style={{ marginTop: 18 }}>
            <div className="panel-head">
              <h3><span className="ic win">🏆</span>Top players</h3>
              <span className="more" onClick={() => navigate('/players')}>Full leaderboard →</span>
            </div>
            <div className="mover-list">
              {topPlayers.map((p, i) => (
                <div key={p.tag} className="mover-row" onClick={() => navigate(`/players/${encodeURIComponent(p.tag)}`)}>
                  <span className="rank" style={{ width: 22 }}>{i + 1}</span>
                  <span className="pav" style={{ width: 34, height: 34, fontSize: 14 }}>{initial(p.name)}</span>
                  <span className="nm">{p.name}</span>
                  <span className="pick">{p.trophies?.toLocaleString() ?? '—'} <span>🏆</span></span>
                </div>
              ))}
            </div>
          </div>
        )}

        {news.length > 0 && (
          <>
            <div className="section-title" style={{ marginTop: 28 }}>
              <span className="mk">📰</span> Latest news
              <span className="more" style={{ marginLeft: 'auto', cursor: 'pointer', color: 'var(--muted)', fontSize: 13.5 }} onClick={() => navigate('/news')}>All news →</span>
            </div>
            <div className="news-list">
              {news.map((p) => (
                <div key={p.slug} className="news-item" onClick={() => navigate(`/news/${p.slug}`)}>
                  <div className="news-title">{p.title}</div>
                  <div className="news-desc">{p.description}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="section-title" style={{ marginTop: 28 }}><span className="mk">🗺</span> Hot maps in rotation</div>
        <div className="map-grid">
          {hotMaps.map((m) => <MapCard key={`${m.map}-${m.mode}`} m={m} />)}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
